import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { useReportData } from "./UseReportData";
import { ChartCard, Empty, KpiBand, PlanActualChart, Seg, SectionFrame, SimpleTable, StatusDot, StatusKey, bucketLabel, bucketTitle, fmt, fmtPct, signed, statusOf, sumAgg, timeMetrics, useChartColors, varPct } from "./Reportkit";

const MET = {
  pa: ["PA", "higher", true], ua: ["UA", "higher", true], oe: ["OE", "higher", true], goh: ["GOH", "higher"], noh: ["NOH", "higher"],
  delay: ["Delay", "lower"], standby: ["Standby", "lower"], bd: ["Breakdown", "lower"],
};

function Body({ d }) {
  const c = useChartColors();
  const families = useMemo(() => [...new Set(d.byModel.map((m) => m.family))], [d]);
  const [family, setFamily] = useState(() => (families.includes("HAUL TRUCK") ? "HAUL TRUCK" : families[0]));
  const [metric, setMetric] = useState("pa");
  const [u, setU] = useState("pct");

  const trend = d.trend.filter((x) => x.family === family);
  const models = d.byModel.filter((m) => m.family === family);
  const tot = sumAgg(models.map((m) => m.agg));
  const A = timeMetrics(tot), P = timeMetrics(tot, true);
  const hasActual = A.cal > 0;
  const [label, better, isPct] = MET[metric];

  const hrs = (x) => (hasActual ? [["pa", A.pa, P.pa]] : []);
  const kpi = (lab, k, b, f, unit = "") => ({ label: lab, value: hasActual ? `${f(A[k])}${unit}` : "—", sub: `Plan ${f(P[k])}${unit}`, status: hasActual ? statusOf(A[k], P[k], b) : "none", delta: hasActual ? varPct(A[k], P[k]) : null });
  const pct = (v) => fmt(v, 1);

  const rows = trend.map((x) => {
    const a = timeMetrics(x.agg), p = timeMetrics(x.agg, true);
    return { label: bucketLabel(x.bucket, d.grain), title: bucketTitle(x.bucket, d.grain), actual: x.agg.cal ? a[metric] : null, plan: p[metric] };
  });

  const parts = [["NOH", "noh", "#0B5D3B"], ["Delay", "delay", "#EDA100"], ["Standby", "standby", "#2A78D6"], ["Breakdown", "bd", "#E34948"]];
  const stack = [["Plan", P], ["Actual", A]].map(([name, m]) => ({ name, ...Object.fromEntries(parts.map(([l, k]) => [l, u === "pct" ? (m.cal ? (m[k] / m.cal) * 100 : 0) : m[k]])) }));

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">FLEET</Typography>
        <Seg value={family} onChange={setFamily} options={families.map((f) => [f, f.replace("HAUL TRUCK", "Haul trucks").replace("LOADING UNIT", "Loading units").replace("AUXILIARY UNIT", "Auxiliary")])} />
        {!hasActual && <Typography variant="caption" color="warning.main">No actual hours recorded for this fleet — plan only (not zero).</Typography>}
      </Box>
      <KpiBand items={[
        kpi("Physical availability", "pa", "higher", pct, "%"), kpi("Use of availability", "ua", "higher", pct, "%"), kpi("Operating efficiency", "oe", "higher", pct, "%"),
        kpi("GOH", "goh", "higher", (v) => fmt(v), " h"), kpi("NOH", "noh", "higher", (v) => fmt(v), " h"), kpi("Delay", "delay", "lower", (v) => fmt(v), " h"),
        kpi("Standby", "standby", "lower", (v) => fmt(v), " h"), kpi("Breakdown", "bd", "lower", (v) => fmt(v), " h"),
      ]} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2 }}>
        <ChartCard title="Fleet KPI trend — plan vs actual" sub={`By ${d.grain} · ${label}`} action={<Seg value={metric} onChange={setMetric} options={Object.entries(MET).map(([k, v]) => [k, v[0]])} />}>
          <PlanActualChart data={rows} better={better} line={!!isPct} valueFmt={isPct ? (v) => fmtPct(v, 1) : (v) => `${fmt(v)} h`} yFmt={isPct ? (v) => `${fmt(v, 0)}%` : undefined} domain={isPct ? ["auto", "auto"] : undefined} />
          <StatusKey />
        </ChartCard>
        <ChartCard title="Where the hours went" sub="Calendar = NOH + Delay + Standby + Breakdown" action={<Seg value={u} onChange={setU} options={[["pct", "% of calendar"], ["h", "Hours"]]} />}>
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={stack} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
              <XAxis dataKey="name" tick={c.tick} /><YAxis tick={c.tick} width={48} tickFormatter={(v) => (u === "pct" ? `${v}%` : fmt(v))} />
              <RTooltip formatter={(v) => (u === "pct" ? fmtPct(v, 1) : `${fmt(v)} h`)} /><Legend />
              {parts.map(([l, , col]) => <Bar key={l} dataKey={l} stackId="h" fill={col} maxBarSize={70} isAnimationActive={false} />)}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>
      <ChartCard title="By equipment model" sub="Hours are recorded per model, not per unit">
        <SimpleTable cols={[
          { key: "model", label: "Model", align: "left" },
          ...["pa", "ua", "oe"].map((k) => ({ key: k, label: k.toUpperCase(), render: (r) => { const a = timeMetrics(r.agg), p = timeMetrics(r.agg, true); return a.cal ? <>{fmtPct(a[k], 1)} <span style={{ opacity: 0.6 }}>(plan {fmt(p[k], 1)}%)</span> <StatusDot status={statusOf(a[k], p[k])} size={12} /></> : "no actual"; } })),
          { key: "goh", label: "GOH", render: (r) => fmt(r.agg.goh) }, { key: "noh", label: "NOH", render: (r) => fmt(r.agg.noh) },
          { key: "dl", label: "Delay", render: (r) => fmt(r.agg.delay) }, { key: "bd", label: "Breakdown", render: (r) => signed(timeMetrics(r.agg).bd, 0, " h").replace("+", "") },
        ]} rows={models} max={300} />
      </ChartCard>
      <Typography variant="caption" color="text.secondary">UA = GOH ÷ Available (business definition). Standby = Available − GOH. Breakdown = Calendar − Available. Per-unit hours are not stored, so unit availability is not shown.</Typography>
    </>
  );
}

export default function EquipmentSection({ req }) {
  const st = useReportData("equipment", req);
  return (
    <SectionFrame eyebrow="Fleet time" title="Equipment Performance" desc="Availability, utilisation and efficiency vs plan, by equipment family and model" state={st}>
      {st.data && (st.data.byModel.length ? <Body d={st.data} /> : <Empty>No fleet-time data for the current filters</Empty>)}
    </SectionFrame>
  );
}